// CSV Loader Utility for React Apps
import Papa from 'papaparse';

/**
 * Load and parse CSV files
 * @param {string} filePath - Path to CSV file
 * @param {Object} options - PapaParse options
 * @returns {Promise} - Parsed CSV data
 */
export const loadCSV = async (filePath, options = {}) => {
  try {
    const response = await fetch(filePath);
    const csvText = await response.text();
    
    return new Promise((resolve, reject) => {
      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: true,
        ...options,
        complete: (results) => {
          resolve(results.data);
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  } catch (error) {
    console.error('Error loading CSV:', error);
    throw error;
  }
};

/**
 * Load multiple CSV files
 * @param {Array} filePaths - Array of CSV file paths
 * @returns {Promise} - Object with parsed data
 */
export const loadMultipleCSVs = async (filePaths) => {
  try {
    const promises = filePaths.map(path => 
      loadCSV(path).then(data => ({ path, data }))
    );
    
    const results = await Promise.all(promises);
    
    return results.reduce((acc, { path, data }) => {
      const fileName = path.split('/').pop().replace('.csv', '');
      acc[fileName] = data;
      return acc;
    }, {});
  } catch (error) {
    console.error('Error loading multiple CSVs:', error);
    throw error;
  }
};

/**
 * Parse CSV string directly
 * @param {string} csvString - CSV string content
 * @param {Object} options - PapaParse options
 * @returns {Promise} - Parsed CSV data
 */
export const parseCSVString = (csvString, options = {}) => {
  return new Promise((resolve, reject) => {
    Papa.parse(csvString, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
      ...options,
      complete: (results) => {
        resolve(results.data);
      },
      error: (error) => {
        reject(error);
      }
    });
  });
};
